import { useState, useRef, useCallback } from 'react'

import { ImagesModal } from '@b-stock/fe-core/bstock-next/components'

import type { MediaType } from '@/buyer-portal/components/features/listing/media/shared/types'
import { useGalleryTracking } from '@/buyer-portal/components/features/listing/media/shared/useGalleryTracking'
import { useVideoTracking } from '@/helpers/video/useVideoTracking'

interface ModalGalleryWithTrackingProps {
  close: () => void
  firstSlide: number
  images: Array<MediaType & { key: string }>
  title: string
  media: MediaType[]
}

export const ModalGalleryWithTracking = ({
  close,
  firstSlide,
  images,
  title,
  media,
}: ModalGalleryWithTrackingProps) => {
  const [modalVideoRefsMap, setModalVideoRefsMap] = useState<
    React.RefObject<ReadonlyMap<number, HTMLIFrameElement>>
  >({ current: new Map() })

  const prevSlideRef = useRef(firstSlide)

  const { analyticsData, trackNavigate } = useGalleryTracking(media)

  // Track video interactions in modal view
  useVideoTracking({
    videoRefsMap: modalVideoRefsMap,
    media,
    analyticsData,
  })

  const handleSlideChange = useCallback(
    ({
      videoRefsMap,
      currentSlide,
    }: {
      videoRefsMap: React.RefObject<ReadonlyMap<number, HTMLIFrameElement>>
      currentSlide: number
    }) => {
      // Update video refs for video tracking
      setModalVideoRefsMap(videoRefsMap)

      // Track slide change only if slide actually changed
      if (currentSlide !== prevSlideRef.current) {
        // ImagesModal doesn't say which control moved it, so infer from the distance
        const distance =
          (currentSlide - prevSlideRef.current + media.length) % media.length
        const isStep = distance === 1 || distance === media.length - 1
        trackNavigate(currentSlide, 'fullscreen', isStep ? 'step' : 'jump')
        prevSlideRef.current = currentSlide
      }
    },
    [media.length, trackNavigate]
  )

  return (
    <ImagesModal
      close={close}
      firstSlide={firstSlide}
      images={images}
      title={title}
      onSlideChange={handleSlideChange}
    />
  )
}
